import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Linkedin, Facebook, Instagram } from 'lucide-react';
import { footerNavigation, contactInfo } from '@/data/navigation';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const SocialIcon = ({ platform }) => {
    const icons = {
      linkedin: Linkedin,
      facebook: Facebook,
      instagram: Instagram,
      pinterest: () => (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
        </svg>
      )
    };
    const Icon = icons[platform.toLowerCase()] || Linkedin;
    return <Icon className="w-5 h-5" />;
  };

  return (
    <footer className="bg-primary-bg text-primary-foreground">
      {/* Main Footer Content */}
      <div className="container-portico py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <img 
                src="/Assets/logos/PS2024_logo_wte_text.svg" 
                alt="Portico Systems" 
                className="h-12 w-auto"
              />
            </Link>
            <p className="text-sm opacity-80 mb-6 max-w-sm">
              Performance Flooring, Built Tough. Designed to Last. Leading commercial flooring solutions for high-traffic environments.
            </p>

            {/* Contact Details */}
            <div className="space-y-3 text-sm">
              <a
                href={`tel:${contactInfo.customerService.phone}`}
                className="flex items-center gap-3 hover:opacity-100 opacity-80 transition-opacity"
              >
                <Phone className="w-4 h-4 flex-shrink-0" />
                <span>{contactInfo.customerService.phone}</span>
              </a>
              <a
                href={`mailto:${contactInfo.customerService.email}`}
                className="flex items-center gap-3 hover:opacity-100 opacity-80 transition-opacity"
              >
                <Mail className="w-4 h-4 flex-shrink-0" />
                <span>{contactInfo.customerService.email}</span>
              </a>
              <div className="flex items-start gap-3 opacity-80">
                <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{contactInfo.headquarters[0].address}</span>
              </div>
              <div className="flex items-center gap-3 opacity-80">
                <Clock className="w-4 h-4 flex-shrink-0" />
                <span>{contactInfo.businessHours.days}: {contactInfo.businessHours.hours}</span>
              </div>
            </div>

            {/* Social Icons */}
            <div className="flex items-center gap-4 mt-6">
              {contactInfo.social.map((social) => (
                <a
                  key={social.platform}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center hover:bg-secondary hover:text-secondary-foreground transition-colors duration-base"
                  aria-label={social.platform}
                >
                  <SocialIcon platform={social.icon} />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading font-semibold mb-4 text-sm uppercase tracking-wider">
              Services
            </h4>
            <ul className="space-y-2">
              {footerNavigation.services.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm opacity-80 hover:opacity-100 transition-opacity"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold mb-4 text-sm uppercase tracking-wider">
              Quick Links
            </h4>
            <ul className="space-y-2">
              {footerNavigation.quickLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm opacity-80 hover:opacity-100 transition-opacity"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* About */}
          <div>
            <h4 className="font-heading font-semibold mb-4 text-sm uppercase tracking-wider">
              About Portico
            </h4>
            <ul className="space-y-2">
              {footerNavigation.about.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    data-testid={`footer-about-${link.label.toLowerCase().replace(/\s+/g, '-')}-link`}
                    className="text-sm opacity-80 hover:opacity-100 transition-opacity"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-primary-light">
        <div className="container-portico py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm opacity-80">
            <p>© {currentYear} Portico Systems. All rights reserved.</p>
            <div className="flex items-center gap-6">
              {footerNavigation.legal.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  className="hover:opacity-100 transition-opacity"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
